/**
 * How to join a virtual setting: the link, meeting ID, passcode or dial-in (Lloyd, 21 Oct 2026:
 * "a reminder to my cell phone ... about 15 minutes before the setting, along with any login
 * information"). Texted to Lloyd with that reminder and in the 7:30 list. Staff only.
 *
 * Saves itself, like the appearance control beside it, so every screen that shows a setting can
 * edit it without its own form knowing about it.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import CourtEventApi from '../../api/CourtEventApi';

interface Props {
  courtEventGuid: string;
  value: string | null | undefined;
  onChanged: (next: string | null) => void;
  disabled?: boolean;
}

const HearingLoginControl: React.FC<Props> = ({ courtEventGuid, value, onChanged, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const next = draft.trim() || null;
      await CourtEventApi.updateCourtEvent({ CourtEvent_guid: courtEventGuid, CourtEvent_LoginInfo: next });
      onChanged(next);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the login.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Box>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
          {value || 'None entered'}
        </Typography>
        <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setEditing(true)} disabled={disabled}>
          {value ? 'Edit login' : 'Add login'}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Link, meeting ID, passcode or dial-in"
        helperText="Texted to Lloyd 15 minutes before a virtual hearing. Staff only."
        disabled={saving}
        inputProps={{ maxLength: 2000 }}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" variant="contained" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default HearingLoginControl;
